import { Component, OnInit, inject, HostListener, signal } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HasPermissionDirective } from '../core/directives/has-permission.directive';
import { AuthenticationService } from '../core/services/authentication.service';
import { AuthService } from '../core/services/auth.service';
import { TenantContextService } from '../core/services/tenant-context.service';
import { TenantsStore } from '../core/stores/tenants.store';
import { BusinessUnitStore } from '../core/stores/business-unit.store';
import { MembersStore } from '../core/stores/members.store';
import { SubscriptionStore } from '../core/stores/subscription.store';
import { SubscriptionBillingService } from '../core/services/subscription-billing.service';
import { SubscriptionStatusModalService } from '../core/services/subscription-status-modal.service';
import { SubscriptionStatusModalComponent } from '../shared/components/subscription-status-modal/subscription-status-modal.component';
import { PlanLimitModalService } from '../core/services/plan-limit-modal.service';
import { PlanLimitModalComponent } from '../shared/components/plan-limit-modal/plan-limit-modal.component';
import { ContextSelectorModalService } from '../core/services/context-selector-modal.service';
import { ContextSelectorModalComponent } from '../shared/components/context-selector-modal/context-selector-modal.component';
import { ContextPillComponent } from '../shared/components/context-pill/context-pill.component';
import { UserService } from '../core/services/user.service';
import { MemberService } from '../core/services/member.service';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet, RouterLink, RouterLinkActive, CommonModule, FormsModule,
    HasPermissionDirective,
    SubscriptionStatusModalComponent,
    PlanLimitModalComponent,
    ContextSelectorModalComponent,
    ContextPillComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css']
})
export class MainLayoutComponent implements OnInit {
  private authService       = inject(AuthenticationService);
  private router            = inject(Router);
  private auth              = inject(AuthService);
  private tenantContext     = inject(TenantContextService);
  private tenantsStore      = inject(TenantsStore);
  private businessUnitStore = inject(BusinessUnitStore);
  private membersStore      = inject(MembersStore);
  private subscriptionStore = inject(SubscriptionStore);
  private subscriptionBilling = inject(SubscriptionBillingService);
  statusModal               = inject(SubscriptionStatusModalService);
  planLimitModal            = inject(PlanLimitModalService);
  contextModal              = inject(ContextSelectorModalService);
  private userService       = inject(UserService);
  private memberService     = inject(MemberService);

  subscriptionQueueCount = signal(0);
  sidebarOpen = signal(false);

  // Own linked member record, if any — lets a plain member's "Payments" nav item
  // point at their own read-only member page instead of the admin /payments list.
  readonly myMemberId = signal<string | null>(null);

  showUserMenu = false;
  avatarUrl: string | null = null;

  get user()                  { return this.auth.user(); }
  get tenant()                { return this.auth.tenant(); }
  get isSuperAdmin()          { return this.auth.isSuperAdmin(); }
  get isTenantAdmin()         { return this.auth.user()?.isTenantAdmin ?? false; }

  get selectedTenantId()      { return this.tenantContext.selectedTenantId(); }
  get selectedBusinessUnitId(){ return this.tenantContext.selectedBusinessUnitId(); }
  get tenants()               { return this.tenantsStore.tenants(); }
  get businessUnits()         { return this.businessUnitStore.activeUnits(); }

  // Fallback for staff who manage payments but have no member record of their own
  // (isSuperAdmin/isTenantAdmin skip straight to the full list; a member with a linked
  // record always sees their own page first, even if they also happen to hold this permission).
  get hasPaymentListPermission(): boolean {
    return this.auth.hasPermission([301, 302], 'any');
  }

  get planName()       { return this.subscriptionStore.planName(); }
  get planStatus()     { return this.subscriptionStore.status(); }
  get planDaysLeft()   { return this.subscriptionStore.daysRemaining(); }

  get planPillClass(): string {
    switch (this.planStatus) {
      case 'Active':        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Trialing':      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Grace':         return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'CancelPending': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'Expired':       return 'bg-red-50 text-red-700 border-red-200';
      case 'Cancelled':     return 'bg-gray-100 text-gray-600 border-gray-200';
      default:              return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  }

  get userInitials(): string {
    const name = this.user?.fullName || this.user?.username || 'U';
    const parts = name.trim().split(' ').filter((p: string) => p);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  get avatarColor(): string {
    const name = this.user?.fullName || this.user?.username || 'User';
    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#84CC16'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  get currentTenantName(): string {
    const id = this.selectedTenantId;
    if (!id) return '';
    const found = this.tenantsStore.tenants().find(t => t.tenantId === id);
    return found?.villageName || this.tenant?.tenantName || id;
  }

  ngOnInit(): void {
    this.loadAvatar();
    if (this.isSuperAdmin) this.loadQueueCount();
    this.memberService.getMyMemberId().subscribe({
      next: (id) => this.myMemberId.set(id),
      error: () => this.myMemberId.set(null)
    });
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.closeSidebar());
    // Tenant + BU stores are loaded lazily by ContextSelectorModalService, which
    // also auto-opens the selector modal when a choice is required.
  }

  private loadQueueCount(): void {
    let total = 0;
    this.subscriptionBilling.countPendingSubmissions().subscribe(res => {
      if (res.success) { total += (res.data as any)?.count ?? 0; this.subscriptionQueueCount.set(total); }
    });
    this.subscriptionBilling.countPendingExemptions().subscribe(res => {
      if (res.success) { total += (res.data as any)?.count ?? 0; this.subscriptionQueueCount.set(total); }
    });
  }

  private loadAvatar(): void {
    const id = this.user?.userId;
    if (!id) return;
    this.userService.getAvatarSas(id).subscribe({
      next: (r) => { this.avatarUrl = r?.success ? (r.data?.url ?? null) : null; },
      error: () => { this.avatarUrl = null; }
    });
  }

  openContextSelector(): void {
    this.showUserMenu = false;
    this.contextModal.open();
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(v => !v);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('[data-user-menu]')) {
      this.showUserMenu = false;
    }
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  onModalDismiss(): void {
    this.statusModal.dismiss();
  }

  onModalLogout(): void {
    this.statusModal.dismiss();
    this.logout();
  }
}
